// A historical quote, read-only (Jacky: "any historical data, if can transform do it, or make it
// read only view, if quote PDF generated, the link to blob can be added direct").
//
// This is the replacement for the link that used to open the old platform's quote page. Nothing here
// is recomputed: the quote was priced by the engine that ran when it was sent, and a historical quote
// is a record of what the customer received — not an input to a new calculation. The PDF, when one was
// generated (128 of 270 quotes have one), is linked **directly at the blob** rather than through the
// platform's page, so it keeps working after the containers are decommissioned.
import {Link, useParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {Page, Panel} from '../shell/Page';
import {DataTable, TableSkeleton, Tag, Td} from '../components/aligned-table';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {aud, day} from '../lib/format';

export type HistoricalQuote = {
  id: string; job_ref: string | null; option_name: string | null; is_primary: boolean;
  subtotal: number | null; gst: number | null; grand_total: number | null;
  status: string | null; created_at: string | null; sent_at: string | null;
  pdf: string | null; pdf_path: string | null; summary: string[];
  lines: {tier: string; text: string}[];
};

export function HistoricalQuotePage() {
  const {id = ''} = useParams();
  const q = useQuery({
    queryKey: ['historical-quote', id],
    queryFn: async () => {
      const r = await fetchImpl(`${env.apiBase}/api/quotes/${encodeURIComponent(id)}`, {headers: await authHeaders()});
      const b = await r.json();
      if (!r.ok) throw Error(b.error ?? 'Quote unavailable');
      return b.data as HistoricalQuote;
    },
  });
  const row = q.data;

  if (q.isPending) return <Page title="Quote"><p role="status" className="text-sm">Reading the quote…</p></Page>;
  if (q.isError || !row) return <Page title="Quote unavailable">
    <p role="alert" className="text-sm text-warning">{q.isError ? (q.error as Error).message : 'not found'}</p>
  </Page>;

  return <Page kicker={`Historical quote · ${row.job_ref ?? ''}`} title={row.option_name ?? 'Quotation'}
    lede={[row.job_ref, row.status, row.created_at ? day(row.created_at) : null].filter(Boolean).join(' · ')}
    actions={row.job_ref ? <Link className="text-primary underline" to={`/jobs/${row.job_ref}`}>Job {row.job_ref}</Link> : undefined}>

    <div className="mb-3 rounded-lg border border-l-4 border-l-muted bg-muted/40 px-4 py-2 text-sm">
      <b>Read only.</b> This quote was priced when it was sent and is kept exactly as it was — nothing on
      this page recalculates it. {row.pdf ? 'The PDF below is the file the customer received.' : 'No PDF was generated for this one.'}
    </div>

    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Panel title="What was quoted" meta={`${row.lines.length} line(s)`}>
        <DataTable cols={[{label: 'Tier', width: 90}, {label: 'Line'}]} density="compact">
          {q.isPending && <TableSkeleton cols={2}/>}
          {row.lines.map((l, i) => <tr key={`${l.text}-${i}`}><Td dim>{l.tier}</Td><Td title={l.text}>{l.text}</Td></tr>)}
          {row.lines.length === 0 && <tr><Td colSpan={2} dim>
            {row.summary.length
              ? `The saved lines are not in a shape this build reads; the summary the quote itself carries is: ${row.summary.join(' · ')}`
              : 'This quote carries no saved lines.'}
          </Td></tr>}
        </DataTable>
      </Panel>

      <div className="space-y-4">
        <Panel title="Totals">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Subtotal</dt><dd className="tabular-nums">{row.subtotal === null ? '—' : aud(row.subtotal)}</dd></div>
            <div className="flex justify-between gap-3 font-semibold"><dt>Total ex GST</dt><dd className="tabular-nums">{row.subtotal === null ? '—' : `${aud(row.subtotal)} + GST`}</dd></div>
            {row.is_primary && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Was</dt><dd><Tag tone="info">primary</Tag></dd></div>}
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Sent</dt><dd>{row.sent_at ? day(row.sent_at) : '—'}</dd></div>
          </dl>
        </Panel>
        <Panel title="The document">
          {row.pdf
            ? <a className="inline-flex min-h-11 items-center rounded-md bg-primary px-4 text-sm text-primary-foreground" href={row.pdf} target="_blank" rel="noreferrer">Open the quote PDF</a>
            : <p className="text-sm text-muted-foreground">No PDF was generated for this quote.</p>}
          {row.pdf_path && <p className="mt-2 break-all font-mono text-micro text-muted-foreground">{row.pdf_path}</p>}
        </Panel>
      </div>
    </div>
  </Page>;
}
